import React, { useState, useCallback, useEffect } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import classnames from 'classnames';
import styles from './textArea.module.scss';
import { substring } from 'runes2';
import type { FC, ChangeEvent, KeyboardEvent, InputHTMLAttributes } from 'react';
import { isEmpty } from 'lodash';

export type TextAreaRef = HTMLTextAreaElement;

type Formatter = (info: { value: string; count: number; maxLength?: number }) => string;

interface ShowCountInterface {
  formatter?: Formatter;
}

export interface TextAreaProps extends Omit<InputHTMLAttributes<TextAreaRef>, 'prefix'> {

  allowClear?: boolean;
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  bordered?: boolean;
  className?: string;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  isEmpty?: boolean;
  maxLength?: number;
  showCount?: ShowCountInterface | boolean;
  style?: React.CSSProperties;

  /**
   * @description 输入框内容变化时的回调		
   */
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onPressEnter?: (e?: KeyboardEvent<HTMLTextAreaElement>) => void;
  /**
   * @description  设置校验状态	
   */
  status?: 'error' | 'warning';

  /**
   * @description 是否禁用状态，默认为 false	
   */
  disabled?: boolean;

}
const TextArea: FC<TextAreaProps> = ({
  allowClear,
  autoSize = true,
  bordered = true,
  className,
  defaultValue,
  maxLength,
  showCount,
  style,
  value,
  onPressEnter,
  placeholder,
  onChange,
  status,
  disabled,
  isEmpty,
  ...resetProps
}) => {

  const prefixCls = 'web_components_textarea';

  const [inputValue, setInputValue] = useState<string | undefined>(defaultValue ?? value);
  const [realValue, setRealValue] = useState<string | undefined>(defaultValue ?? value);
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    setInputValue(value);
  }, [value])
 
  const handleClear = useCallback(() => {
    setInputValue('');
  }, []);

  useEffect(() => {
    if (!isComposing) {
      setRealValue(inputValue);
    }
  }, [inputValue, isComposing])

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      if (isEmpty) {
        e.target.value = e.target.value?.replace(/[\r\n]+/g,'');
      }

      const _event = e;

      if (maxLength && !isComposing) {
        _event.target.value = substring(e.target.value, 0, maxLength);
      }

      const { value: newValue } = _event.target;
      setInputValue(newValue);
      onChange?.(e);
    },

    [setInputValue, isComposing, isEmpty]
  );

  const handleCompositionStart = () => {
    setIsComposing(true);
    
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
    if (maxLength && inputValue) {
      setInputValue(substring(inputValue, 0, maxLength));
    }
  };

  const handleKeyPress = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && typeof onPressEnter === 'function') {
        onPressEnter(e);
      }
    },
    [onPressEnter],
  );

  const sharedTextAreaProps = {
    className: classnames(prefixCls, { [`${prefixCls}_bordered`]: bordered }, className),
    disabled: false,
    maxLength,
    onChange: handleChange,
    onKeyPress: handleKeyPress,
    value: inputValue,
    placeholder,
    ...resetProps
  };

  let countElement = null;

  if (typeof showCount === 'boolean' && showCount) {
    countElement = (
      <span className={`${prefixCls}_count`}>{`${(realValue ?? '').length}${maxLength ? `/${maxLength}` : ''
        }`}</span>
    );
  } else {
    if (typeof showCount !== 'boolean' && showCount?.formatter && typeof showCount?.formatter === 'function') {
      countElement = (
        <span className={`${prefixCls}_count`}>
          {showCount.formatter({ value: realValue ?? '', count: (realValue ?? '').length, maxLength })}
        </span>
      );
    }
  }

  const clearElement = allowClear && inputValue ? (
    <button type="button" className="textArea__clear" onClick={handleClear}>
      X
    </button>
  ) : null;

  const renderTextarea = () => {
    if (autoSize) {
      return <TextareaAutosize  rows={1} {...sharedTextAreaProps} onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd} />;
    }
    return <textarea {...sharedTextAreaProps} onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd} style={style} />;
  }

  return (
    <div className={
      classnames(styles[`${prefixCls}_wrapper`], {
        [styles[`${prefixCls}_disabled`]]: disabled,
        [styles[`${prefixCls}_${status}`]]: status
      })
    }>
      <div className={`${prefixCls}_container`}>
        {renderTextarea()}
        {clearElement}
      </div>
      {countElement}
    </div>
  );
};
export default TextArea;
